// VendorAppsPanel.js — one connected app's surface in the visit view: its
// status line, Refresh, and the viewport the host positions its frame over.
// With `appId` it shows just that app (a pinned app's own tab, or the app open
// inside the Apps tab — the launcher there draws the tiles); without one it
// keeps its own row of app tiles. Frames belong to VendorAppHost (mounted in
// layouts/User.js), so switching tabs hides the frame; it does not reload it.
import { useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import { GlobalContext } from '../../api/GlobalContext';
import useVendorStore from '../../utils/vendorApps/useVendorStore';
import { VIEWPORT_ID } from './VendorAppHost';
import { isTpiApp } from '../../utils/vendorApps/store';

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null);

function statusFor(store, app) {
  const status = store.statusFor(app.id);
  const buffer = store.getBuffer(app.id);
  const ours = store.patient?.name;
  if (status.lastError?.reason === 'NAME_MISMATCH') {
    return { tone: 'danger', text: `${app.name} has ${status.lastError.theirs} open; Comprehend has ${status.lastError.ours}. Not linked.` };
  }
  // A packaged adapter says what it needs from the clinician (sign in, open a
  // client…) here rather than in a banner inside its frame.
  if (status.appStatus?.text) {
    return { tone: { error: 'danger', action: 'warning' }[status.appStatus.kind] || 'muted', text: status.appStatus.text };
  }
  if (!ours) {
    const held = store.pendingAssignFor(app.id);
    return held ? { tone: 'muted', text: `This visit has no patient. ${app.name} has ${held.name}${held.dob ? ` (DOB ${held.dob})` : ''} open.` } : { tone: 'muted', text: 'No chart open.' };
  }
  if (buffer) {
    const subs = store.subsFor(app.id).size;
    return { tone: 'muted', text: `${ours} ↔ ${buffer.patientName} · provided ${fmtTime(buffer.providedAt)}${subs ? ` · ${subs} subscription${subs === 1 ? '' : 's'}` : ''}` };
  }
  return { tone: 'muted', text: `Open ${ours} in ${app.name} to link.` };
}

/**
 * TPI scrolls inside its own frame, so the page must not scroll as well. While
 * it's shown, the tab bar is scrolled to the top and the page is held there,
 * and the frame fills the rest of the window: one scroll, TPI's. Record and
 * Comprehend are above; any other tab gives the page its scroll back. What sits
 * between the tab bar and the frame is measured, not assumed. null when off.
 */
function useFittedHeight(on, rootRef) {
  const [height, setHeight] = useState(null);
  useEffect(() => {
    if (!on) {
      setHeight(null);
      return undefined;
    }
    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    let frame = null;
    const measure = () => {
      const viewport = document.getElementById(VIEWPORT_ID);
      const nav = rootRef.current?.closest('.tab-content')?.previousElementSibling;
      if (!viewport || !nav) return;
      const navTop = nav.getBoundingClientRect().top + window.scrollY;
      const above = viewport.getBoundingClientRect().top - nav.getBoundingClientRect().top;
      const next = Math.max(320, Math.floor(window.innerHeight - above));
      setHeight((prev) => (prev !== null && Math.abs(prev - next) <= 1 ? prev : next));
      // Held with the tab bar at the top; overflow hidden still lets us place it.
      root.style.overflow = 'hidden';
      if (Math.abs(window.scrollY - navTop) > 1) window.scrollTo({ top: navTop });
    };
    // Our own height change resizes the body too; measuring again then is a no-op.
    const schedule = () => {
      clearTimeout(frame);
      frame = setTimeout(measure, 50);
    };
    measure();
    window.addEventListener('resize', schedule);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    observer?.observe(document.body);
    return () => {
      clearTimeout(frame);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
      root.style.overflow = previousOverflow;
    };
  }, [on, rootRef]);
  return height;
}

export default function VendorAppsPanel({ active = true, appId = null }) {
  const { apiClient } = useContext(GlobalContext);
  const store = useVendorStore();
  const apps = store.apps.filter((a) => a.enabled);
  const rootRef = useRef(null);

  // Opening an app hands it the panel: scroll so the tab bar sits at the top
  // and the frame below fills the rest (Record / Comprehend are one scroll up).
  // Not for TPI, whose frame is sized so the page never scrolls.
  // Only ever scrolls down to it, never back up past where the clinician is.
  // The tab's fade-in can leave the page too short to scroll on the first
  // frame (the pane isn't laid out yet), so it retries briefly until the tab
  // bar can actually reach the top.
  useEffect(() => {
    if (!active) return undefined;
    let tries = 0;
    let timer = null;
    const attempt = () => {
      if (isTpiApp((appId && store.getApp(appId)) || store.getApp(store.activeAppId))) return;
      const nav = rootRef.current?.closest('.tab-content')?.previousElementSibling;
      if (!nav) return;
      const top = nav.getBoundingClientRect().top + window.scrollY;
      if (window.scrollY >= top - 2) return;
      const reachable = document.documentElement.scrollHeight - window.innerHeight >= top - 2;
      if (reachable || tries >= 6) {
        window.scrollTo({ top, behavior: 'smooth' });
        return;
      }
      tries += 1;
      timer = setTimeout(attempt, 80);
    };
    // A timer, not requestAnimationFrame: rAF never fires in a hidden page.
    timer = setTimeout(attempt, 0);
    return () => clearTimeout(timer);
  }, [active, appId]);

  // Pick something when the tab is first shown — the app that was showing for
  // this visit earlier in the tab if we remember one — and record that the
  // provider has now opened it (so it eager-mounts on later visits).
  useEffect(() => {
    if (!active || !apps.length) return;
    if (appId && store.activeAppId !== appId) store.setActiveApp(appId);
    else if (!store.activeAppId) store.setActiveApp(store.recallActiveApp() || apps[0].id);
    if (store.activeAppId) store.markUsed(store.activeAppId);
  }, [active, apps, appId, store, store.activeAppId]);

  // Make sure the list is loaded — from the profile if it has arrived, else
  // straight from the backend. Re-checked whenever the tab is shown, so an
  // admin's change (or a slow profile) never leaves this tab saying "no apps".
  useEffect(() => {
    if (active) store.ensureApps(apiClient);
  }, [active, apiClient, store]);

  // TPI scrolls inside its own frame, so the frame must fit the window exactly
  // once the tab bar is at the top (the scroll above): one scroll, TPI's.
  const shown = (appId && store.getApp(appId)) || store.getApp(store.activeAppId) || apps[0];
  const fitHeight = useFittedHeight(active && isTpiApp(shown), rootRef);

  if (apps.length === 0) {
    return (
      <p className="text-muted small p-2 mb-0">
        No apps enabled for your group. An admin can add one under Admin Settings → Apps.
      </p>
    );
  }

  const activeApp = (appId && store.getApp(appId)) || store.getApp(store.activeAppId) || apps[0];
  const status = statusFor(store, activeApp);
  // The app reports its content height (comprehend:height); the frame is sized to it, so
  // there is one scroll — the page's — not a frame scrolling inside a page.
  const contentHeight = isTpiApp(activeApp) ? null : store.statusFor(activeApp.id).contentHeight;

  const linkAction = store.pendingAssignFor(activeApp.id)
    ? { label: `Use ${activeApp.name}'s patient`, title: `Use ${activeApp.name}'s patient for this visit`, run: () => store.assignFromApp(activeApp.id) }
    : store.pendingFor(activeApp.id)
      ? { label: 'Link them', title: 'Yes, same patient: link them', run: () => store.confirmLink(activeApp.id) }
      : null;
  const linked = store.patient && store.getRef(activeApp.id, store.patient.id) != null;
  // Apps that support zoom report it (comprehend:zoom); the clinician steps it here.
  const zoom = store.statusFor(activeApp.id).zoom || null;
  const setZoom = (value) => store.requestZoom(activeApp.id, value == null ? null : Math.round(value * 100) / 100);
  const inlineButton = { border: 0, background: 'transparent', padding: 0, fontSize: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap', flex: 'none' };

  return (
    <div ref={rootRef} className="d-flex flex-column">
      {!appId && (
        <div className="d-flex align-items-start flex-wrap px-1 pt-1" style={{ gap: 2 }}>
          {apps.map((app) => {
            // An app, not a button: a launcher tile — the vendor's icon (or a brand-coloured
            // monogram), name beneath, brand ring when it's the one on screen.
            const isActive = app.id === activeApp.id;
            const brand = app.color || '#5e72e4';
            return (
              <button
                key={app.id}
                type="button"
                onClick={() => store.setActiveApp(app.id)}
                title={app.description || app.name}
                aria-pressed={isActive}
                className="vendor-app-tile"
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 72, padding: '6px 4px', border: 0, background: 'transparent', cursor: 'pointer', opacity: isActive ? 1 : 0.72 }}
              >
                <span style={{ width: 44, height: 44, borderRadius: 12, background: app.icon ? '#fff' : brand, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, boxShadow: isActive ? `0 0 0 2px #fff, 0 0 0 4px ${brand}` : '0 1px 2px rgba(0,0,0,.12)' }}>
                  {app.icon ? <img src={app.icon} alt="" width="44" height="44" style={{ objectFit: 'cover', display: 'block', borderRadius: 12 }} /> : (app.name || '?').slice(0, 1).toUpperCase()}
                </span>
                <span style={{ fontSize: 11, fontWeight: isActive ? 700 : 500, color: isActive ? brand : '#525f7f', maxWidth: 72, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{app.name}</span>
              </button>
            );
          })}
        </div>
      )}
      {/* One low line above the app: who it's linked to (or what's needed), the
          one action that applies, and Refresh as an icon. The app gets the rest. */}
      <div className="d-flex align-items-center" style={{ gap: 8, minHeight: 24, padding: '0 2px 4px', fontSize: 12, lineHeight: 1.3 }}>
        <span className={`text-${status.tone}`} title={status.text} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {status.text}
        </span>
        {linkAction && (
          <button type="button" title={linkAction.title} onClick={linkAction.run} style={{ ...inlineButton, color: '#14724F', fontWeight: 600 }}>
            {linkAction.label}
          </button>
        )}
        {!linkAction && linked && (
          <button
            type="button"
            title={`Forget the link between ${store.patient.name} and ${activeApp.name}'s record`}
            onClick={() => store.unlink(activeApp.id)}
            style={{ ...inlineButton, color: '#8898aa' }}
          >
            Unlink
          </button>
        )}
        {zoom && (
          <span className="d-inline-flex align-items-center" style={{ flex: 'none', color: '#8898aa' }} role="group" aria-label={`${activeApp.name} zoom`}>
            <button type="button" aria-label="Zoom out" title="Zoom out" disabled={zoom.value <= zoom.min + 1e-9} onClick={() => setZoom(zoom.value - zoom.step)} style={{ ...inlineButton, width: 20, height: 22, color: 'inherit', fontSize: 15, opacity: zoom.value <= zoom.min + 1e-9 ? 0.35 : 1 }}>−</button>
            <button type="button" aria-label="Reset zoom" title="Reset zoom" onClick={() => setZoom(null)} style={{ ...inlineButton, minWidth: 34, color: 'inherit', fontVariantNumeric: 'tabular-nums' }}>{Math.round(zoom.value * 100)}%</button>
            <button type="button" aria-label="Zoom in" title="Zoom in" disabled={zoom.value >= zoom.max - 1e-9} onClick={() => setZoom(zoom.value + zoom.step)} style={{ ...inlineButton, width: 20, height: 22, color: 'inherit', fontSize: 15, opacity: zoom.value >= zoom.max - 1e-9 ? 0.35 : 1 }}>+</button>
          </span>
        )}
        <button
          type="button"
          aria-label="Refresh apps"
          disabled={!store.patient}
          title={store.patient ? 'Refresh: re-run every app’s subscriptions against this visit' : 'Open a visit first'}
          onClick={() => {
            store.ensureApps(apiClient, { force: true }); // pick up apps an admin just added
            store.requestEvaluate('refresh');
          }}
          style={{ ...inlineButton, width: 22, height: 22, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#8898aa', opacity: store.patient ? 1 : 0.4 }}
        >
          <i className="fa fa-sync-alt" style={{ fontSize: 11 }} aria-hidden="true" />
        </button>
      </div>
      {/* The host positions the active iframe over this box while the tab is
          visible. It fills the panel below the tab bar (once scrolled there);
          an app that reports a taller content height gets that instead. */}
      {active && (
        <div
          id={VIEWPORT_ID}
          className="border rounded"
          style={{
            height: fitHeight ? `${fitHeight}px` : contentHeight ? `max(${contentHeight}px, calc(100vh - 96px))` : 'calc(100vh - 96px)',
            minHeight: 320,
            maxWidth: '100vw',
            background: '#fff',
            transition: 'height .15s ease-out',
          }}
        />
      )}
    </div>
  );
}
